import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic } from "lucide-react";
import { useTranslation } from "react-i18next";

const COLORS = ["#0f172a", "#dc2626", "#ea580c", "#16a34a", "#0284c7", "#7c3aed"];
const SIZES = [["2", "S"], ["3", "M"], ["5", "L"]];

const STYLE_RE = /^\s*(color|font-size)\s*:[^;<>]+;?\s*$/i;

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
  if (node.hasAttribute?.("style") && !STYLE_RE.test(node.getAttribute("style") || "")) {
    node.removeAttribute("style");
  }
});

export function sanitizeRichHtml(html) {
  return DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "br", "p", "div", "span", "font", "ul", "ol", "li", "a"],
    ALLOWED_ATTR: ["color", "size", "style", "href", "target", "rel"],
  });
}

export function RichTextView({ html, className = "" }) {
  return <div className={`rich-text-view break-words ${className}`} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }} />;
}

export function RichTextEditor({ value, onChange, testId, minHeight = 96 }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const lastEmitted = useRef(null);
  useEffect(() => {
    if (ref.current && value !== lastEmitted.current) {
      ref.current.innerHTML = value || "";
      lastEmitted.current = value || "";
    }
  }, [value]);
  const emit = () => {
    if (!ref.current) return;
    lastEmitted.current = ref.current.innerHTML;
    onChange(ref.current.innerHTML);
  };
  const cmd = (c, v = null) => {
    ref.current?.focus();
    document.execCommand(c, false, v);
    emit();
  };
  const btn = "rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800";
  return (
    <div className="rounded-md border border-slate-200 focus-within:ring-2 focus-within:ring-sky-500">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50/60 px-2 py-1">
        <button type="button" className={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("bold")} title={t("common.bold")} data-testid={`${testId}-bold`}>
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" className={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("italic")} title={t("common.italic")} data-testid={`${testId}-italic`}>
          <Italic className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        {SIZES.map(([size, label]) => (
          <button key={size} type="button" className={`${btn} px-2 font-mono text-xs font-bold`} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("fontSize", size)} title={`${t("common.fontSize")}: ${label}`} data-testid={`${testId}-size-${label.toLowerCase()}`}>
            {label}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        {COLORS.map((c) => (
          <button key={c} type="button" className="h-4 w-4 rounded-full border border-slate-300 transition-transform hover:scale-110" style={{ backgroundColor: c }} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd("foreColor", c)} title={t("common.textColor")} data-testid={`${testId}-color-${c.slice(1)}`} />
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        className="w-full overflow-y-auto px-3 py-2 text-sm text-slate-800 outline-none"
        style={{ minHeight }}
        onInput={emit}
        onBlur={emit}
        data-testid={testId}
      />
    </div>
  );
}
